// FuelConsumptionTimelineChart - Time series chart showing fuel consumption (km/L) per vehicle

import { useState, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  formatDateShort,
  formatNumber,
  formatDecimal,
} from "../../utils/formatters";

// Color palette for different vehicles
const COLORS = [
  "#ef4444", "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6",
  "#ec4899", "#06b6d4", "#f97316", "#14b8a6", "#6366f1",
  "#84cc16", "#e11d48", "#0ea5e9", "#a855f7", "#22c55e",
  "#d946ef", "#0891b2", "#ea580c", "#059669", "#7c3aed",
  "#db2777", "#2563eb", "#16a34a", "#ca8a04", "#9333ea",
];

const FuelConsumptionTimelineChart = ({ consumptionData }) => {
  const [selectedPlates, setSelectedPlates] = useState([]);
  const [viewMode, setViewMode] = useState("multi"); // "multi" = multi-line, "single" = per vehicle

  // Get unique license plates
  const licensePlates = useMemo(() => {
    const plates = [...new Set(consumptionData.map((d) => d.license_plate))];
    return plates.sort();
  }, [consumptionData]);

  // Initialize selected plates (all) on first render
  useMemo(() => {
    if (selectedPlates.length === 0 && licensePlates.length > 0) {
      setSelectedPlates(licensePlates.slice(0, 5)); // Show first 5 by default
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licensePlates]);

  // Color mapping
  const plateColors = useMemo(() => {
    const map = {};
    licensePlates.forEach((plate, i) => {
      map[plate] = COLORS[i % COLORS.length];
    });
    return map;
  }, [licensePlates]);

  // Aggregate distance+volume per plate per date → km/L = total_distance / total_volume
  const aggregateByDatePlate = useMemo(() => {
    const agg = {}; // { dateKey: { plate: { distance, volume } } }
    consumptionData.forEach((d) => {
      if (!agg[d.date]) agg[d.date] = {};
      if (!agg[d.date][d.license_plate]) agg[d.date][d.license_plate] = { distance: 0, volume: 0 };
      agg[d.date][d.license_plate].distance += d.distance || 0;
      agg[d.date][d.license_plate].volume += d.oil_volume || 0;
    });
    return agg;
  }, [consumptionData]);

  // Prepare multi-line chart data: aggregate same-day records per plate
  const multiLineData = useMemo(() => {
    const dateMap = {};

    consumptionData
      .filter((d) => selectedPlates.includes(d.license_plate))
      .forEach((d) => {
        const dateKey = d.date;
        if (!dateMap[dateKey]) {
          dateMap[dateKey] = { date: dateKey, dateShort: formatDateShort(dateKey) };
        }
      });

    // Fill km/L using aggregated distance/volume per plate per date
    Object.keys(dateMap).forEach((dateKey) => {
      selectedPlates.forEach((plate) => {
        const agg = aggregateByDatePlate[dateKey]?.[plate];
        if (agg && agg.volume > 0) {
          dateMap[dateKey][plate] = Math.round((agg.distance / agg.volume) * 100) / 100;
        }
      });
    });

    return Object.values(dateMap).sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );
  }, [consumptionData, selectedPlates, aggregateByDatePlate]);

  // Prepare single vehicle data: aggregate same-day records
  const singleVehicleData = useMemo(() => {
    if (viewMode !== "single" || selectedPlates.length === 0) return [];

    const plate = selectedPlates[0];
    const dates = [...new Set(
      consumptionData.filter((d) => d.license_plate === plate).map((d) => d.date)
    )].sort();

    return dates.map((date) => {
      const agg = aggregateByDatePlate[date]?.[plate] || { distance: 0, volume: 0 };
      const rate = agg.volume > 0 ? Math.round((agg.distance / agg.volume) * 100) / 100 : 0;
      return {
        date,
        dateShort: formatDateShort(date),
        license_plate: plate,
        distance: agg.distance,
        oil_volume: agg.volume,
        consumption_rate: rate,
      };
    });
  }, [consumptionData, selectedPlates, viewMode, aggregateByDatePlate]);

  // Calculate averages per plate: total_distance / total_volume (volume-weighted)
  const plateAverages = useMemo(() => {
    const avgs = {};
    selectedPlates.forEach((plate) => {
      const records = consumptionData.filter((d) => d.license_plate === plate);
      if (records.length > 0) {
        const totalDistance = records.reduce((s, r) => s + (r.distance || 0), 0);
        const totalVolume = records.reduce((s, r) => s + (r.oil_volume || 0), 0);
        avgs[plate] = totalVolume > 0 ? Math.round((totalDistance / totalVolume) * 100) / 100 : 0;
      }
    });
    return avgs;
  }, [consumptionData, selectedPlates]);

  // Toggle plate selection
  const togglePlate = (plate) => {
    setSelectedPlates((prev) => {
      if (prev.includes(plate)) {
        return prev.filter((p) => p !== plate);
      }
      return [...prev, plate];
    });
  };

  const selectAll = () => setSelectedPlates([...licensePlates]);
  const deselectAll = () => setSelectedPlates([]);

  // Custom tooltip
  const MultiLineTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border-2 border-gray-300 rounded-lg shadow-xl max-w-xs">
          <p className="text-sm font-bold text-gray-900 mb-2 border-b pb-1">
            {payload[0]?.payload?.date || label}
          </p>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {payload
              .sort((a, b) => (b.value || 0) - (a.value || 0))
              .map((entry, index) => (
                <div key={index} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-1.5">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: entry.color }}
                    />
                    <span className="text-xs text-gray-700">{entry.name}</span>
                  </div>
                  <span className="text-xs font-bold" style={{ color: entry.color }}>
                    {formatDecimal(entry.value, 2)} km/L
                  </span>
                </div>
              ))}
          </div>
        </div>
      );
    }
    return null;
  };

  const SingleLineTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 border-2 border-gray-300 rounded-lg shadow-xl">
          <p className="text-sm font-bold text-gray-900 mb-2">
            {data.date}
          </p>
          <div className="space-y-1">
            <p className="text-sm text-indigo-600 font-semibold">
              อัตราสิ้นเปลือง: {formatDecimal(data.consumption_rate, 2)} km/L
            </p>
            <p className="text-sm text-gray-600">
              เลขไมล์: {formatNumber(data.prev_mileage)} → {formatNumber(data.mileage)} km
            </p>
            <p className="text-sm text-emerald-600 font-semibold">
              ระยะทาง: {formatNumber(data.distance)} km
            </p>
            <p className="text-sm text-blue-600 font-semibold">
              น้ำมัน: {formatDecimal(data.oil_volume)} ลิตร
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  if (!consumptionData || consumptionData.length === 0) {
    return (
      <div className="bg-white rounded-xl p-8 text-center border-2 border-gray-200">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <p className="text-gray-600 font-medium">
            ไม่มีข้อมูลอัตราการใช้น้ำมัน
          </p>
          <p className="text-gray-400 text-sm mt-1">
            ต้องมีข้อมูลเลขไมล์อย่างน้อย 2 ครั้งต่อป้ายทะเบียน
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* View Mode Toggle */}
      <div className="flex flex-wrap gap-3 justify-center">
        <button
          onClick={() => setViewMode("multi")}
          className={`px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 ${
            viewMode === "multi"
              ? "bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-lg transform scale-105"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          <span className="flex items-center gap-2">📊 เปรียบเทียบรถ</span>
        </button>
        <button
          onClick={() => {
            setViewMode("single");
            if (selectedPlates.length === 0 && licensePlates.length > 0) {
              setSelectedPlates([licensePlates[0]]);
            } else if (selectedPlates.length > 1) {
              setSelectedPlates([selectedPlates[0]]);
            }
          }}
          className={`px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 ${
            viewMode === "single"
              ? "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg transform scale-105"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          <span className="flex items-center gap-2">🚌 รายคัน</span>
        </button>
      </div>

      {/* Vehicle Selection */}
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h5 className="text-sm font-bold text-gray-700">
            เลือกป้ายทะเบียน ({selectedPlates.length}/{licensePlates.length})
          </h5>
          {viewMode === "multi" && (
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="px-3 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
              >
                เลือกทั้งหมด
              </button>
              <button
                onClick={deselectAll}
                className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                ล้างทั้งหมด
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {licensePlates.map((plate) => (
            <button
              key={plate}
              onClick={() => {
                if (viewMode === "single") {
                  setSelectedPlates([plate]);
                } else {
                  togglePlate(plate);
                }
              }}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all duration-200 border-2 ${
                selectedPlates.includes(plate)
                  ? "text-white shadow-md transform scale-105"
                  : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
              }`}
              style={
                selectedPlates.includes(plate)
                  ? {
                      backgroundColor: plateColors[plate],
                      borderColor: plateColors[plate],
                    }
                  : {}
              }
            >
              {plate}
              {plateAverages[plate] !== undefined && selectedPlates.includes(plate) && (
                <span className="ml-1 opacity-90">
                  ({formatDecimal(plateAverages[plate], 2)} km/L)
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Average Summary Cards */}
      {selectedPlates.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {selectedPlates.map((plate) => {
            const records = consumptionData.filter((d) => d.license_plate === plate);
            const avg = plateAverages[plate] || 0;
            const totalDist = records.reduce((s, r) => s + r.distance, 0);
            const totalVol = records.reduce((s, r) => s + r.oil_volume, 0);

            return (
              <div
                key={plate}
                className="bg-white rounded-lg p-3 border-l-4 shadow-sm"
                style={{ borderLeftColor: plateColors[plate] }}
              >
                <p className="text-xs font-bold text-gray-800">{plate}</p>
                <p
                  className="text-lg font-bold mt-1"
                  style={{ color: plateColors[plate] }}
                >
                  {formatDecimal(avg, 2)} <span className="text-xs font-normal text-gray-500">km/L</span>
                </p>
                <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                  <p>{formatNumber(totalDist)} km | {formatDecimal(totalVol)} L</p>
                  <p>{records.length} ครั้ง</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Chart */}
      {viewMode === "multi" && selectedPlates.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
          <h5 className="text-lg font-bold text-gray-800 mb-4">
            อัตราการใช้น้ำมัน (km/L) เปรียบเทียบรายคัน
          </h5>
          <ResponsiveContainer width="100%" height={450}>
            <LineChart
              data={multiLineData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="dateShort"
                angle={-45}
                textAnchor="end"
                height={80}
                tick={{ fontSize: 11, fill: "#6B7280" }}
                stroke="#9CA3AF"
              />
              <YAxis
                tick={{ fontSize: 12, fill: "#6B7280" }}
                stroke="#9CA3AF"
                label={{
                  value: "km/L",
                  angle: -90,
                  position: "insideLeft",
                  style: { textAnchor: "middle", fill: "#6B7280", fontSize: 12 },
                }}
              />
              <Tooltip content={<MultiLineTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: "20px", fontSize: "12px" }}
              />
              {selectedPlates.map((plate) => (
                <Line
                  key={plate}
                  type="monotone"
                  dataKey={plate}
                  name={plate}
                  stroke={plateColors[plate]}
                  strokeWidth={2}
                  dot={{ fill: plateColors[plate], r: 3 }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Single Vehicle Detailed Chart */}
      {viewMode === "single" && selectedPlates.length === 1 && singleVehicleData.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
          <h5 className="text-lg font-bold text-gray-800 mb-4">
            อัตราการใช้น้ำมัน - รถทะเบียน {selectedPlates[0]}
          </h5>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={singleVehicleData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="dateShort"
                angle={-45}
                textAnchor="end"
                height={80}
                tick={{ fontSize: 11, fill: "#6B7280" }}
                stroke="#9CA3AF"
              />
              <YAxis
                tick={{ fontSize: 12, fill: "#6B7280" }}
                stroke="#9CA3AF"
                label={{
                  value: "km/L",
                  angle: -90,
                  position: "insideLeft",
                  style: { textAnchor: "middle", fill: "#6B7280", fontSize: 12 },
                }}
              />
              <Tooltip content={<SingleLineTooltip />} />
              <Line
                type="monotone"
                dataKey="consumption_rate"
                name="อัตราสิ้นเปลือง (km/L)"
                stroke={plateColors[selectedPlates[0]]}
                strokeWidth={3}
                dot={{ fill: plateColors[selectedPlates[0]], r: 4 }}
                activeDot={{ r: 7 }}
              />
            </LineChart>
          </ResponsiveContainer>

          {/* Detail Table */}
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">วันที่</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700">เลขไมล์ก่อนหน้า</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700">เลขไมล์ล่าสุด</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700">ระยะทาง (km)</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700">น้ำมัน (ลิตร)</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700">อัตรา (km/L)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {singleVehicleData.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-900">{row.dateShort}</td>
                    <td className="px-4 py-2 text-right text-gray-600">
                      {formatNumber(row.prev_mileage)}
                    </td>
                    <td className="px-4 py-2 text-right text-indigo-600 font-semibold">
                      {formatNumber(row.mileage)}
                    </td>
                    <td className="px-4 py-2 text-right text-emerald-600 font-semibold">
                      {formatNumber(row.distance)}
                    </td>
                    <td className="px-4 py-2 text-right text-blue-600 font-semibold">
                      {formatDecimal(row.oil_volume)}
                    </td>
                    <td className="px-4 py-2 text-right font-bold"
                      style={{ color: plateColors[row.license_plate] }}
                    >
                      {formatDecimal(row.consumption_rate, 2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default FuelConsumptionTimelineChart;
